// Module local imports
import { LinkDataLayerObject } from './_data-layer-types';
import {
  gtmDatalayerPush,
  isEmailLink,
  findParentCategory,
  getComponentType,
  findEventType,
} from './_helpers';

/**
 * Helper class for processing client-side link click events
 */
export class GtmLinkHelper {
  /**
   * Client-side link clicked callback.  Used for links contained in a sitecore content block
   */
  linkClicked({ target }: PointerEvent) {
    if (target instanceof HTMLAnchorElement) {
      const componentType = getComponentType(target) ?? 'N/A';
      const contentCategory = findParentCategory(target) ?? 'N/A';
      const linkEventType = findEventType(target);
      if (isEmailLink(target)) {
        const clickText = target.firstChild?.nodeValue?.replace('@', '_at_');
        gtmDatalayerPush({
          event: linkEventType,
          clickText: `${clickText}`,
          contentCategory,
          componentType,
        } as LinkDataLayerObject);
      } else {
        const clickText = target.firstChild?.nodeValue;
        gtmDatalayerPush({
          event: linkEventType,
          clickText: clickText?.trim() ?? 'N/A',
          linkUrl: target.href,
          contentCategory,
          componentType,
        } as LinkDataLayerObject);
      }
    }
  }
}

/**
 * Extends Window object with a GtmLinkHelper
 */
export interface WindowWithGtmLinkHelper extends Window {
  gtmClickHelper?: GtmLinkHelper;
}

/**
 * Creates a window-global GtmClickHelper and returns a strongly typed value.
 */
export const withGtmLinkHelper = (window?: Window): WindowWithGtmLinkHelper | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }
  const myWindow = window as WindowWithGtmLinkHelper;
  myWindow.gtmClickHelper = myWindow.gtmClickHelper ?? new GtmLinkHelper();
  return myWindow;
};
